import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";

/**
 * Guard zarządzający kontrolą dostępu do modułu Regulaminów i Zgód (Terms of Agreement).
 * Weryfikuje po stronie serwera API, czy zalogowany użytkownik posiada uprawnienia
 * prawne i administracyjne do zarządzania politykami prywatności oraz treściami zgód.
 */
@Injectable({
  providedIn: 'root'
})
export class TermsOfAgreementAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) kodu JavaScript
   * powiązanego z modułem zgód. Minimalizuje wektor ataku poprzez zablokowanie
   * transferu wrażliwej logiki biznesowej do przeglądarki klienta przed
   * pozytywną autoryzacją na serwerze API.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku posiadania odpowiedniej roli).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/terms-of-agreement-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - terms-of-agreement - ok');
      return true;
    }));
  }

  /**
   * Weryfikuje uprawnienia tuż przed fizyczną aktywacją i wyrenderowaniem interfejsu
   * modułu zgód. Gwarantuje integralność sesji poprzez asynchroniczną obsługę błędów
   * wygaśnięcia tokena (HTTP 401) oraz deleguje proces wyświetlania komunikatów
   * odmowy dostępu do ustandaryzowanej metody z klasy CommonUtils.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub bezpieczne drzewo routingu (UrlTree).
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/terms-of-agreement-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - canActivate - terms-of-agreement - ok', response);

      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

}
